import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import Button from '../../../components/Button';
import CalloutCard from '../../../components/CalloutCard';
import DefaultCard from '../../../components/DefaultCard';
import ExpandableContentEstimatePlan from '../../../components/ExpandableContentEstimatePlan';
import GenericTable from '../../../components/GenericTable';
import IconButton from '../../../components/IconButton';
import Link from '../../../components/Link';
import ModalHeader from '../../../components/ModalHeader';
import { RadioGroup } from '../../../components/Radio';
import SelectableCard from '../../../components/SelectableCard';
import UserCard from '../../../components/UserCards';
import { EnrollmentStore } from '../../../providers/enrollment';
import { getAllAccidentInsuranceBenefits } from '../../../services/enrollment';
import { Profile } from '../../../types';
import { formatPrice, openExternalLink } from '../../../utils/utils';

export type customAccidentInsuranceBenefitsModalProps = {
  showAccidentInsuranceBenefitsModal: boolean;
  handleCloseAccidentInsuranceBenefitsModal: () => void;
};

const buttonForCalloutCard = [
  <Button
    variant="secondary-outline"
    rightIcon={light('clipboard-medical')}
    className="callout-button"
    size="sm"
    onClick={() =>
      openExternalLink(
        'https://qabtr.lifeatworkportal.com/dst/findprovider/?dataoverride=1'
      )
    }
  >
    Accident Insurance
  </Button>
];

// todo: this data need to be extracted form medical.json file after GenerricTable comp refactor
const tableData = {
  headers: ['Plan', 'You', 'You & Spouse', 'You & Children', 'You & Family'],
  rows: [
    [
      'Enhanced',
      formatPrice(4),
      formatPrice(6.91),
      formatPrice(8.9),
      formatPrice(11.48),
    ],
    [
      'Basic',
      formatPrice(2),
      formatPrice(3.46),
      formatPrice(4.45),
      formatPrice(5.74),
    ],
  ]
};

const AccidentInsuranceBenefitsModal = ({
  showAccidentInsuranceBenefitsModal,
  handleCloseAccidentInsuranceBenefitsModal,
}: customAccidentInsuranceBenefitsModalProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showTableData, setShowTableData] = useState<boolean>(false);
  const [accidentInsuranceBenefits, setAccidentInsuranceBenefitsPlans] = useState<any>({});
  const [radioButtonValue, setRadioButtonValue] = useState<string | number>(3);
  const {
    activeHealthBenefits,
    updateCost,
    updatePlanName,
    accidentInsuranceSelectedProfiles,
    updateSelectedProfiles,
  } = useContext(EnrollmentStore);

  const selectedTableIndex = useMemo(() => {
    const selectedProfilesRelationships = accidentInsuranceSelectedProfiles.map(
      (profiles: any) => {
        return profiles.relationship.toLowerCase();
      }
    );

    const uniqueRelationships = new Set(selectedProfilesRelationships);
    const sortedRelationships = Array.from(uniqueRelationships).sort(
      (r1: any, r2: any) => (r1 < r2 ? -1 : 1)
    );
    const concatenatedRelationships = sortedRelationships.join('-');

    switch (concatenatedRelationships) {
      case 'self':
        return 1;
      case 'self-spouse':
        return 2;
      case 'child-self':
        return 3;
      case 'child-self-spouse':
        return 4;
      default:
        return -1;
    }
  }, [accidentInsuranceSelectedProfiles]);

  const dataPlans = useMemo(() => {
    return getAllAccidentInsuranceBenefits();
  }, []);

  useEffect(() => {
    if (dataPlans) {
      setAccidentInsuranceBenefitsPlans(dataPlans);
    }
  }, [dataPlans]);

  useEffect(() => {
    if (selectedTableIndex > -1) {
      const newPlansPrices = dataPlans['plans'].map((plan: any) => {
        if (plan.id.includes('enh')) {
          plan.cost = +tableData['rows'][0][selectedTableIndex].replace(
            '$',
            ''
          );
        } else if (plan.id.includes('bas')) {
          plan.cost = +tableData['rows'][1][selectedTableIndex].replace('$', '');
        }

        return plan;
      });
      setAccidentInsuranceBenefitsPlans({
        ...dataPlans,
        plans: newPlansPrices,
      });
    }
  }, [dataPlans, selectedTableIndex]);

  useEffect(() => {
    const currentBenefit = activeHealthBenefits.find(
      (benefit: any) => benefit.type === 'accident-insurance'
    );
    const index = accidentInsuranceBenefits.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setRadioButtonValue(index);
  }, [activeHealthBenefits, accidentInsuranceBenefits]);

  const handleRadioButtonValue = (e: string | number) => {
    setRadioButtonValue(e);
  };

  const dataProfiles = activeHealthBenefits;
  const accidentInsurancePlan = dataProfiles.filter((plan: any) => plan.type === 'accident-insurance');

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const toggleTableData = () => {
    setShowTableData(!showTableData);
  };

  if (!accidentInsurancePlan.length) {
    return null;
  }

  const planProfiles = accidentInsurancePlan[0]['profiles'];

  return (
    <Modal
      show={showAccidentInsuranceBenefitsModal}
      onHide={handleCloseAccidentInsuranceBenefitsModal}
      dialogClassName="modal-xl"
      className={'medical-benefits-modal'}
    >
      <ModalHeader
        leftIcon={light('clipboard-medical')}
        leftIconVariant="icon-emphasized"
        rightButton={
          <div onClick={toggleDetails} className={'icon-button-wrapper'}>
            <IconButton
              variant="icon-default"
              icon={light('circle-info')}
              extLabel="Benefit Info"
            />
          </div>
        }
      >
        <h4 className={'text-sm-20-lg-32'}>Accident Insurance</h4>
      </ModalHeader>
      <Modal.Body className={'pt-4'}>
        <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
          <div className="d-flex justify-content-center">
            <DefaultCard
              closeButton={
                <Link 
                  className='pt-0' 
                  isAlignAtEnd 
                  isWhiteIcon 
                  rightIcon={light('close')}
                  onClick={toggleDetails}
                />                      
              }
              variant="blue-motif"
              leftHeading={
                accidentInsuranceBenefits['generic']
                  ? accidentInsuranceBenefits['generic']['info']['title']
                  : ''
              }
              leftSubheading={
                <Container className={'px-0 d-flex flex-column'}>
                  {accidentInsuranceBenefits['generic'] ? (
                    <>
                      <span>
                        {accidentInsuranceBenefits['generic']['info']['description']}
                      </span>
                    </>
                  ) : (
                    ''
                  )}
                </Container>
              }
              className={'mb-12'}
            />
          </div>
        </Row>
        <h5 className={'h5'}>Who would you like to cover?</h5>
        <Row className={'mt-10 mt-lg-11 mx-0'}>
          {planProfiles.map((profile: Profile, index: number) => (
            <UserCard
              item={profile}
              key={index}
              selected={accidentInsuranceSelectedProfiles.find(
                (e: any) => e.id === profile.id
              )}
              className={'mb-7 mb-lg-0 me-lg-7'}
              onClick={() => updateSelectedProfiles('accident-insurance', profile)}
            />
          ))}
        </Row>
        <Row className={'mt-11 mt-lg-12 mx-0'}>
          <CalloutCard
            cardIcon={light('screwdriver-wrench')}
            buttons={buttonForCalloutCard}
          >
            <Row className="content-row text-ligther-grey lh-xs">
              <span className="bolder-text ">
                Would you like to explore your plan options in more detail?
              </span>
            </Row>
          </CalloutCard>
        </Row>
        {!showTableData && (
          <Fragment>
            <div
              className={
                'mt-11 mt-lg-12 mx-0 d-flex justify-content-between align-items-center'
              }
            >
              <h5 className={'h5 mb-0'}>Which plan would you like?</h5>
              <Link isBlue onClick={toggleTableData}>
                View cost across plans
              </Link>
            </div>
            {accidentInsuranceBenefits ? (
              <div className={'d-flex flex-column mt-7 mt-lg-11 mx-0'}>
                <RadioGroup
                  name={'medical-plans'}
                  value={radioButtonValue}
                  onChange={handleRadioButtonValue}
                >
                  {accidentInsuranceBenefits['plans']
                    ? accidentInsuranceBenefits['plans'].map(
                        (
                          plan: {
                            title: string;
                            subtitle2: string;
                            cost: any;
                            costType: string;
                            costEstimation: [];
                          },
                          index: any
                        ) => (
                          <SelectableCard
                            title={plan.title}
                            subtitle2={plan.subtitle2}
                            prePaydPeriod={plan.cost}
                            radioButtonValue={index}
                            prePaydPeriodSubtitle={
                              plan.costType !== 'none'
                                ? `${
                                    accidentInsuranceSelectedProfiles.flat().length
                                  } persons, per-pay-period`
                                : ''
                            }
                            selected={radioButtonValue === index}
                            disabled={
                              index === accidentInsuranceBenefits['plans'].length - 1
                            }
                            key={index}
                          >
                            {plan.costEstimation !== null && (
                              <ExpandableContentEstimatePlan
                                breakdownData={plan.costEstimation}
                              />
                            )}
                          </SelectableCard>
                        )
                      )
                    : ''}
                </RadioGroup>
              </div>
            ) : (
              'NO DATA'
            )}
          </Fragment>
        )}
        {showTableData && (
          <Row className={'mt-7 mt-lg-11'}>
            <div className={'d-flex align-items-center'}>
              <FontAwesomeIcon icon={light('arrow-left')} />
              <h5 className={'h5 mb-0 ms-4'}>Plan coverage level costs</h5>
            </div>
            <Col className={'mt-10 overflow-auto'}>
              <GenericTable
                selectedColumnIndex={selectedTableIndex}
                tableData={tableData}
              />
            </Col>
            <div className={'mt-10'}>
              <Button
                variant="primary"
                leftIcon={light('arrow-left')}
                onClick={toggleTableData}
              >
                Back to Plan Options
              </Button>
            </div>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary-outline"
          onMouseDown={handleCloseAccidentInsuranceBenefitsModal}
          className={'mt-7 mt-lg-0'}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onMouseDown={() => {
            updateCost('accident-insurance', accidentInsuranceBenefits.plans[radioButtonValue]);
            updatePlanName('accident-insurance', accidentInsuranceBenefits.plans[radioButtonValue]);
            handleCloseAccidentInsuranceBenefitsModal();
          }}
        >
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccidentInsuranceBenefitsModal;
